import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { TreeProvider } from './contexts/TreeContext';
import AppLayout from './components/layout/AppLayout';
import './styles/global.css';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const TreePage = lazy(() => import('./pages/tree/TreePage'));
const ViewTreePage = lazy(() => import('./pages/tree/ViewTreePage'));
const FeedPage = lazy(() => import('./pages/feed/FeedPage'));
const MilestonesPage = lazy(() => import('./pages/milestones/MilestonesPage'));
const MemorialPage = lazy(() => import('./pages/memorial/MemorialPage'));
const PublicMemorialPage = lazy(() => import('./pages/memorial/PublicMemorialPage'));
const InvitePage = lazy(() => import('./pages/invite/InvitePage'));
const FoundingInvitePage = lazy(() => import('./pages/founding/FoundingInvitePage'));
const FoundingCirclePage = lazy(() => import('./pages/founding/FoundingCirclePage'));
const FamilyPage = lazy(() => import('./pages/family/FamilyPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const PersonProfilePage = lazy(() => import('./pages/person/PersonProfilePage'));
const PaymentsPage = lazy(() => import('./pages/payments/PaymentsPage'));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));
const LegacyPage = lazy(() => import('./pages/legacy/LegacyPage'));
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTickets = lazy(() => import('./pages/admin/AdminTickets'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminAdmins = lazy(() => import('./pages/admin/AdminAdmins'));
const AdminPricing = lazy(() => import('./pages/admin/AdminPricing'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminReferrals = lazy(() => import('./pages/admin/AdminReferrals'));
const AdminFoundingCodes = lazy(() => import('./pages/admin/AdminFoundingCodes'));
const ReferralPage = lazy(() => import('./pages/referral/ReferralPage'));
const ReferralLandingPage = lazy(() => import('./pages/referral/ReferralLandingPage'));
const GreetingCardPage = lazy(() => import('./pages/greetings/GreetingCardPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const DeleteAccountPage = lazy(() => import('./pages/legal/DeleteAccountPage'));
const ChildSafetyPage = lazy(() => import('./pages/legal/ChildSafetyPage'));
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage'));

const LazyAdminProvider = lazy(() =>
  import('./pages/admin/useAdmin').then(m => ({ default: m.AdminProvider }))
);

const AdminWithProvider = () => (
  <Suspense fallback={<PageLoader />}>
    <LazyAdminProvider>
      <AdminLayout />
    </LazyAdminProvider>
  </Suspense>
);

const PageLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '60vh', color: 'var(--text-dim)', fontSize: 14,
    fontFamily: "'Source Sans 3',sans-serif",
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }}>🌳</div>
      <div>Loading...</div>
    </div>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <TreeProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />
                <Route path="/child-safety" element={<ChildSafetyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />

                <Route element={<AppLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/tree" element={<TreePage />} />
                  <Route path="/view-tree/:treeId" element={<ViewTreePage />} />
                  <Route path="/feed" element={<FeedPage />} />
                  <Route path="/milestones" element={<MilestonesPage />} />
                  <Route path="/memorial" element={<MemorialPage />} />
                  <Route path="/legacy" element={<LegacyPage />} />
                  <Route path="/family" element={<FamilyPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/person/:personId" element={<PersonProfilePage />} />
                  <Route path="/payments" element={<PaymentsPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/referrals" element={<ReferralPage />} />
                  <Route path="/greeting-cards" element={<GreetingCardPage />} />
                  <Route path="/founding-circle" element={<FoundingCirclePage />} />
                </Route>

                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route element={<AdminWithProvider />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/tickets" element={<AdminTickets />} />
                  <Route path="/admin/reports" element={<AdminReports />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/admins" element={<AdminAdmins />} />
                  <Route path="/admin/pricing" element={<AdminPricing />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/referrals" element={<AdminReferrals />} />
                  <Route path="/admin/founding-codes" element={<AdminFoundingCodes />} />
                </Route>

                <Route path="/memorial/:personId" element={<PublicMemorialPage />} />
                <Route path="/invite/:code" element={<InvitePage />} />
                <Route path="/founding/:code" element={<FoundingInvitePage />} />
                <Route path="/invite/ref/:code" element={<ReferralLandingPage />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
          </TreeProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
